/**
 * Stable practitioner → display color assignment for multi-calendar mode.
 * Display-only — never persisted to FHIR resources.
 */

/** Default Mantine-friendly palette (bg/border pairs as CSS variable stems). */
export const PRACTITIONER_CALENDAR_COLOR_PALETTE = [
  { bg: 'var(--mantine-color-blue-1)', border: 'var(--mantine-color-blue-6)', swatch: 'var(--mantine-color-blue-6)' },
  { bg: 'var(--mantine-color-teal-1)', border: 'var(--mantine-color-teal-6)', swatch: 'var(--mantine-color-teal-6)' },
  { bg: 'var(--mantine-color-violet-1)', border: 'var(--mantine-color-violet-6)', swatch: 'var(--mantine-color-violet-6)' },
  { bg: 'var(--mantine-color-orange-1)', border: 'var(--mantine-color-orange-6)', swatch: 'var(--mantine-color-orange-6)' },
  { bg: 'var(--mantine-color-pink-1)', border: 'var(--mantine-color-pink-6)', swatch: 'var(--mantine-color-pink-6)' },
  { bg: 'var(--mantine-color-cyan-1)', border: 'var(--mantine-color-cyan-6)', swatch: 'var(--mantine-color-cyan-6)' },
  { bg: 'var(--mantine-color-grape-1)', border: 'var(--mantine-color-grape-6)', swatch: 'var(--mantine-color-grape-6)' },
  { bg: 'var(--mantine-color-lime-1)', border: 'var(--mantine-color-lime-7)', swatch: 'var(--mantine-color-lime-7)' },
  { bg: 'var(--mantine-color-indigo-1)', border: 'var(--mantine-color-indigo-6)', swatch: 'var(--mantine-color-indigo-6)' },
  { bg: 'var(--mantine-color-yellow-1)', border: 'var(--mantine-color-yellow-7)', swatch: 'var(--mantine-color-yellow-7)' },
] as const;

export type PractitionerCalendarColor = (typeof PRACTITIONER_CALENDAR_COLOR_PALETTE)[number];

/**
 * Assign colors for selected practitioners.
 *
 * - `currentPractitionerId` always receives palette[0] when selected.
 * - Additional IDs receive colors in selection order.
 * - `previousAssignment` preserves colors for practitioners that remain selected
 *   (removing one does not recolor the rest during the session).
 */
export function assignPractitionerCalendarColors(args: {
  selectedIds: readonly string[];
  currentPractitionerId: string;
  previousAssignment?: ReadonlyMap<string, PractitionerCalendarColor>;
}): Map<string, PractitionerCalendarColor> {
  const result = new Map<string, PractitionerCalendarColor>();
  const previous = args.previousAssignment;
  const used = new Set<PractitionerCalendarColor>();

  const ordered = [...args.selectedIds];
  const selfIdx = ordered.indexOf(args.currentPractitionerId);
  if (selfIdx > 0) {
    ordered.splice(selfIdx, 1);
    ordered.unshift(args.currentPractitionerId);
  }

  const takeNextFree = (preferIndex: number): PractitionerCalendarColor => {
    for (let offset = 0; offset < PRACTITIONER_CALENDAR_COLOR_PALETTE.length; offset += 1) {
      const color =
        PRACTITIONER_CALENDAR_COLOR_PALETTE[
          (preferIndex + offset) % PRACTITIONER_CALENDAR_COLOR_PALETTE.length
        ];
      if (!used.has(color)) {
        used.add(color);
        return color;
      }
    }
    const fallback =
      PRACTITIONER_CALENDAR_COLOR_PALETTE[preferIndex % PRACTITIONER_CALENDAR_COLOR_PALETTE.length];
    used.add(fallback);
    return fallback;
  };

  if (ordered.includes(args.currentPractitionerId)) {
    const selfColor = PRACTITIONER_CALENDAR_COLOR_PALETTE[0];
    result.set(args.currentPractitionerId, selfColor);
    used.add(selfColor);
  }

  let nextIndex = 1;
  for (const id of ordered) {
    if (result.has(id)) continue;
    const prev = previous?.get(id);
    if (prev && !used.has(prev)) {
      result.set(id, prev);
      used.add(prev);
      continue;
    }
    const color = takeNextFree(nextIndex);
    result.set(id, color);
    nextIndex += 1;
  }

  return result;
}

export function isMultiPractitionerColorMode(selectedCount: number): boolean {
  return selectedCount >= 2;
}
